import React, { useEffect, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { FaRupeeSign } from "react-icons/fa";
import * as Yup from "yup";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import "./receiptModule.css";
import Navbar from "../navbar/navbar";
import {
    getAllEkadasiList,
    getGotraMasterList,
    getJaThaPraMasterList,
    getNakshatraMasterList,
    getPoojaMasterList,
} from "../../services/admin.service";
import {
    formatISODate,
    generateReceipt,
    getBase64ImageFromUrl,
    getDevoteeMasterData,
} from "../../services/receipt.service";
import ReceiptPrint from "../../print/receiptPrint";
import headerLogo from "../../assets/images/DailyPoojaReport.png";
import PaymentModalComponent from "./paymentModalComponent";
import { openRazorpayCheckout } from "./razorpayCheckout";
import {
    toNumber,
    getMinDate,
    getMaxDate,
    hasPaymentDetails,
    loadMasterList,
    buildPaymentDto,
    commonDevoteeSchema,
    MobileEntryCard,
    PaymentLoader,
    DevoteeDetailsSection,
    PaymentDetailsPreview,
    GrandTotal,
    PaymentTypeSelect,
    SubmitBar,
    getDefaultGotra,
    getDefaultNakshatra,
} from "./receiptShared";

const GenerateReceiptComponent = () => {
    const roleName = localStorage.getItem("roleName");
    const username = localStorage.getItem("username");
    const isUser = roleName === "ROLE_USER";
    const isAdmin = roleName === "ROLE_ADMIN";
    const receiptsRoute = isUser ? "/user-receipts" : "/print-receipts";
    const navigate = useNavigate();

    const [mobileEntered, setMobileEntered] = useState(false);
    const [mobileNumber, setMobileNumber] = useState("");
    const [gotraMasterList, setGotraMasterList] = useState([]);
    const [nakshatraMasterList, setNakshatraMasterList] = useState([]);
    const [poojaMasterList, setPoojaMasterList] = useState([]);
    const [ekadasiMasterList, setEkadasiMasterList] = useState([]);
    const [jaThaData, setJaThaPraData] = useState(null);
    const [devoteeData, setDevoteeData] = useState(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    /* ------------------------- data loading ------------------------- */

    useEffect(() => {
        const fetchData = async () => {
            if (isUser && username) {
                setMobileNumber(username);
                setMobileEntered(true);
                try {
                    const result = await getDevoteeMasterData(username);
                    setDevoteeData(result || {});
                    const response = await getJaThaPraMasterList();
                    setJaThaPraData(response?.[0] || {});
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        loadMasterList(getGotraMasterList, setGotraMasterList, "Gotra");
        loadMasterList(getNakshatraMasterList, setNakshatraMasterList, "Nakshatra");
        loadMasterList(getPoojaMasterList, setPoojaMasterList, "pooja");
        loadMasterList(getAllEkadasiList, setEkadasiMasterList, "ekadasi");
    }, []);

    const handleMobileSubmit = async () => {
        if (mobileNumber.length !== 10) {
            Swal.fire("Error", "Please enter a valid 10-digit mobile number", "error");
            return;
        }
        setLoading(true);
        try {
            let result = null;
            try {
                result = await getDevoteeMasterData(mobileNumber);
            } catch (err) {
                console.warn("Devotee not found or error fetching devotee details:", err);
                result = null;
            }
            setDevoteeData(result || {});
            const response = await getJaThaPraMasterList();
            setJaThaPraData(response?.[0] || {});
            setMobileEntered(true);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Could not load details. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const isDevoteeNameDisabled = Boolean(devoteeData?.nameOfDevotee && devoteeData.nameOfDevotee.trim());

    /* ------------------------- form config ------------------------- */

    const initialValues = {
        devoteeName: devoteeData?.nameOfDevotee || "",
        mobile: devoteeData?.mobileNo || mobileNumber,
        alternateMobile: devoteeData?.alternateNo || "",
        nakshatra: getDefaultNakshatra(devoteeData, nakshatraMasterList),
        gotra: getDefaultGotra(devoteeData, gotraMasterList),
        poojaType1: "",
        poojaDateTime1: null,
        poojaType2: "",
        poojaDateTime2: null,
        poojaAmount: 0,
        jaThaPraPersons: 0,
        jaThaPraRate: 0,
        masterJaThaPraRate: jaThaData?.rate || 0,
        extraRemarks: "",
        extraPersons: 0,
        extraRate: 0,
        paymentType: isUser ? "Online" : "",
        paymentDetails: {},
    };

    const nonNegativeNumber = Yup.number().nullable().min(0, "Must be >= 0").typeError("Must be a number");

    const validationSchema = Yup.object({
        ...commonDevoteeSchema,
        poojaType1: Yup.string().required("Pooja Type 1 is required"),
        poojaDateTime1: Yup.string().nullable().required("Pooja Date & Time 1 is required"),
        poojaAmount: Yup.number().min(0, "Must be >= 0"),
        jaThaPraPersons: nonNegativeNumber,
        jaThaPraRate: nonNegativeNumber,
        extraPersons: nonNegativeNumber,
        extraRate: nonNegativeNumber,
    });

    const nakshatraOptions = nakshatraMasterList.map((item) => ({
        value: item.nakshatraId,
        label: item.nakshatraName,
    }));

    const gotraOptions = gotraMasterList.map((item) => ({
        value: item.gotraId,
        label: item.gotraName,
    }));

    const poojaOptions = poojaMasterList.map((item) => ({
        value: item.poojaId,
        label: `${item.poojaName} - ${item.poojaTime}`,
    }));

    /* ------------------------- calculations ------------------------- */

    const calculateGrandTotal = (values) => {
        const jaTotal = toNumber(values.jaThaPraPersons) * toNumber(values.masterJaThaPraRate);
        return toNumber(values.poojaAmount) + jaTotal + toNumber(values.extraRate);
    };

    const calculateTotalPersons = (values, poojaTypeList) => {
        const pooja1 = poojaTypeList.find((p) => p.poojaId === parseInt(values.poojaType1));
        const pooja2 = poojaTypeList.find((p) => p.poojaId === parseInt(values.poojaType2));
        const persons1 = pooja1 ? pooja1.noOfPersons : 0;
        const persons2 = pooja2 ? pooja2.noOfPersons : 0;
        return persons1 + persons2 + toNumber(values.jaThaPraPersons) + toNumber(values.extraPersons);
    };

    const calculatePoojaAmount = (pooja1, pooja2, poojaTypeList) => {
        const data1 = poojaTypeList.find((p) => p.poojaId === parseInt(pooja1));
        const data2 = poojaTypeList.find((p) => p.poojaId === parseInt(pooja2));
        return (data1 ? data1.poojaAmount : 0) + (data2 ? data2.poojaAmount : 0);
    };

    const buildReceiptData = (values) => ({
        ...values,
        jaThaPraRate: toNumber(values.jaThaPraPersons) * toNumber(values.masterJaThaPraRate),
        totalAmount: calculateGrandTotal(values),
        totalPerson: calculateTotalPersons(values, poojaMasterList),
    });

    /* ------------------------- handlers ------------------------- */

    const handleDateChange = (date, fieldName, setFieldValue) => {
        if (!date) {
            setFieldValue(fieldName, null);
            return;
        }
        const selectedDateStr = date.toLocaleDateString("en-CA");
        const ekadasi = ekadasiMasterList.find((item) => item.ekadasiDate === selectedDateStr);
        if (ekadasi) {
            Swal.fire({
                icon: "warning",
                title: "Ekadasi Alert!",
                text: `${ekadasi.ekadasiRemarks} – No Seva allowed.`,
            });
            setFieldValue(fieldName, null);
        } else {
            setFieldValue(fieldName, formatISODate(date));
        }
    };

    const handlePaymentTypeChange = (e, setFieldValue) => {
        const value = e.target.value;
        setFieldValue("paymentType", value);
        setSelectedPaymentType(value);
        if (["CQ", "RT"].includes(value)) {
            setShowModal(true);
        } else {
            setFieldValue("paymentDetails", {});
        }
    };

    const handleCancel = (resetForm) => {
        if (resetForm) resetForm();
        setMobileEntered(false);
        setMobileNumber("");
        setDevoteeData(null);
        setSelectedPaymentType("");
    };

    const finishAndLeave = (resetForm) => {
        resetForm();
        setMobileEntered(false);
        setMobileNumber("");
        setDevoteeData(null);
        navigate(receiptsRoute);
    };

    const handleSubmit = async (values, { resetForm }) => {
        if (!values.paymentType) {
            Swal.fire("Payment Type Required", "Please select a payment type.", "warning");
            return;
        }

        // ---- Cash / Cheque / RTGS ----
        if (["CA", "CQ", "RT"].includes(values.paymentType)) {
            const requiresDetails = ["CQ", "RT"].includes(values.paymentType);
            if (requiresDetails && !hasPaymentDetails(values.paymentDetails)) {
                Swal.fire("Payment Details Required", "Please enter the cheque/RTGS payment details.", "warning");
                return;
            }

            const confirmResult = await Swal.fire({
                title: "Generate Receipt?",
                text: "Are you sure you want to generate this receipt?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Generate",
                cancelButtonText: "Cancel",
            });
            if (!confirmResult.isConfirmed) return;

            setLoading(true);
            try {
                const response = await generateReceipt(buildReceiptData(values));
                if (!response) {
                    Swal.fire("Error", "Failed to generate receipt. Please try again.", "error");
                    return;
                }
                const base64Logo = await getBase64ImageFromUrl(headerLogo);
                await ReceiptPrint(response, base64Logo, poojaMasterList, nakshatraMasterList, gotraMasterList);
                finishAndLeave(resetForm);
            } catch (error) {
                console.error("Error generating receipt:", error);
                Swal.fire("Error", "Error generating receipt. Please try again.", "error");
            } finally {
                setLoading(false);
            }
            return;
        }

        // ---- Online (Razorpay) ----
        if (values.paymentType === "Online") {
            const grandTotal = calculateGrandTotal(values);
            if (!grandTotal || grandTotal <= 0) {
                Swal.fire("Invalid Amount", "Total amount must be greater than zero for online payment.", "warning");
                return;
            }

            const onPaymentSuccess = async (paymentData, pdfWindow) => {
                setLoading(true);
                try {
                    const receiptData = {
                        ...buildReceiptData(values),
                        paymentDetails: buildPaymentDto(paymentData),
                    };
                    const response = await generateReceipt(receiptData);
                    if (!response) {
                        if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                        Swal.fire("Error", "Failed to generate receipt. Please contact temple office.", "error");
                        return;
                    }
                    const base64Logo = await getBase64ImageFromUrl(headerLogo);
                    await ReceiptPrint(response, base64Logo, poojaMasterList, nakshatraMasterList, gotraMasterList, pdfWindow);
                    finishAndLeave(resetForm);
                } catch (error) {
                    console.error("Error generating receipt after payment:", error);
                    if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                    Swal.fire("Error", "Payment succeeded, but receipt generation encountered an error. Please contact temple office.", "error");
                } finally {
                    setLoading(false);
                }
            };

            await openRazorpayCheckout(
                grandTotal,
                values,
                setLoading,
                onPaymentSuccess,
                "Pooja Receipt Payment"
            );
            return;
        }

        Swal.fire("Invalid Payment Type", "Please select a valid payment type.", "warning");
    };

    /* ------------------------- render ------------------------- */

    return (
        <div className="card receipt-page">
            <Navbar />
            <div className="receipt-heading">
                <h3 className="custom-heading">Generate Receipt</h3>
            </div>

            <div className="receipt-content">
                {!mobileEntered ? (
                    <MobileEntryCard
                        mobileNumber={mobileNumber}
                        setMobileNumber={setMobileNumber}
                        onSubmit={handleMobileSubmit}
                    />
                ) : (
                    <>
                        {loading && <PaymentLoader text="Processing, please wait..." />}
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize={true}
                        >
                            {({ setFieldValue, values, resetForm }) => (
                                <>
                                    <Form autoComplete="off">
                                        <div className="card receipt-form-card">
                                            <DevoteeDetailsSection
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                nakshatraOptions={nakshatraOptions}
                                                gotraOptions={gotraOptions}
                                                isNameDisabled={isDevoteeNameDisabled}
                                                showCancel={!isUser}
                                                onCancel={() => handleCancel(resetForm)}
                                            />

                                            <hr className="my-4" />
                                            <h4 className="section-title pooja">Pooja Details</h4>
                                            <div className="row g-3 text-start">
                                                <div className="col-md-3">
                                                    <label className="form-label">Pooja Type 1</label>
                                                    <Select
                                                        className="text-start"
                                                        classNamePrefix="rs"
                                                        options={poojaOptions}
                                                        value={poojaOptions.find((opt) => opt.value === Number(values.poojaType1)) || null}
                                                        onChange={(selected) => {
                                                            const poojaId = selected ? selected.value : "";
                                                            setFieldValue("poojaType1", poojaId);
                                                            setFieldValue(
                                                                "poojaAmount",
                                                                calculatePoojaAmount(poojaId, values.poojaType2, poojaMasterList)
                                                            );
                                                        }}
                                                        isClearable
                                                        placeholder="Select"
                                                    />
                                                    <ErrorMessage name="poojaType1" component="div" className="text-danger" />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label">Pooja Type 1 Date</label>
                                                    <DatePicker
                                                        id="poojaDateTime1"
                                                        name="poojaDateTime1"
                                                        placeholderText="Select Date"
                                                        selected={values.poojaDateTime1}
                                                        onChange={(date) => handleDateChange(date, "poojaDateTime1", setFieldValue)}
                                                        className="form-control"
                                                        dateFormat="dd-MM-yyyy"
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        minDate={getMinDate()}
                                                        maxDate={getMaxDate()}
                                                        onKeyDown={(event) => event.preventDefault()}
                                                    />
                                                    <ErrorMessage name="poojaDateTime1" component="div" className="text-danger" />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Pooja Type 2</label>
                                                    <Select
                                                        className="text-start"
                                                        classNamePrefix="rs"
                                                        options={poojaOptions}
                                                        value={poojaOptions.find((opt) => opt.value === Number(values.poojaType2)) || null}
                                                        onChange={(selected) => {
                                                            const poojaId = selected ? selected.value : "";
                                                            setFieldValue("poojaType2", poojaId);
                                                            setFieldValue(
                                                                "poojaAmount",
                                                                calculatePoojaAmount(values.poojaType1, poojaId, poojaMasterList)
                                                            );
                                                        }}
                                                        isClearable
                                                        placeholder="Select"
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label">Pooja Type 2 Date</label>
                                                    <DatePicker
                                                        id="poojaDateTime2"
                                                        name="poojaDateTime2"
                                                        placeholderText="Select Date"
                                                        selected={values.poojaDateTime2}
                                                        onChange={(date) => handleDateChange(date, "poojaDateTime2", setFieldValue)}
                                                        className="form-control"
                                                        dateFormat="dd-MM-yyyy"
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        minDate={getMinDate()}
                                                        maxDate={getMaxDate()}
                                                        onKeyDown={(event) => event.preventDefault()}
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label">Total Pooja Amount</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text"><FaRupeeSign /></span>
                                                        <Field name="poojaAmount" className="form-control" type="number" readOnly />
                                                    </div>
                                                </div>
                                            </div>

                                            <hr className="my-4" />
                                            <div className="row g-4">
                                                <div className="col-md-6 pe-md-4 border-end">
                                                    <h4 className="section-title additional">Additional JaThaPra</h4>
                                                    <div className="row g-3 text-start">
                                                        <div className="col-sm-4">
                                                            <label className="form-label">No. of Persons</label>
                                                            <Field type="number" name="jaThaPraPersons" className="form-control" />
                                                            <ErrorMessage name="jaThaPraPersons" component="div" className="text-danger" />
                                                        </div>
                                                        <div className="col-sm-4">
                                                            <label className="form-label">Rate per Person</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text"><FaRupeeSign /></span>
                                                                <Field name="masterJaThaPraRate" className="form-control" type="number" readOnly />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-4">
                                                            <label className="form-label">Total</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text"><FaRupeeSign /></span>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    value={toNumber(values.jaThaPraPersons) * toNumber(values.masterJaThaPraRate)}
                                                                    readOnly
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-6 ps-md-4">
                                                    <h4 className="section-title additional">Extra Special For ThaPra</h4>
                                                    <div className="row g-3 text-start">
                                                        <div className="col-sm-6">
                                                            <label className="form-label">Remarks</label>
                                                            <Field name="extraRemarks" className="form-control" />
                                                        </div>
                                                        <div className="col-sm-3 col-6">
                                                            <label className="form-label">No. of Persons</label>
                                                            <Field type="number" name="extraPersons" className="form-control" />
                                                            <ErrorMessage name="extraPersons" component="div" className="text-danger" />
                                                        </div>
                                                        <div className="col-sm-3 col-6">
                                                            <label className="form-label">Amount</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text"><FaRupeeSign /></span>
                                                                <Field type="number" name="extraRate" className="form-control" />
                                                            </div>
                                                            <ErrorMessage name="extraRate" component="div" className="text-danger" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {(isAdmin || isUser) && (
                                                <div className="row g-3 mt-4 align-items-end">
                                                    <div className="col-md-6">
                                                        {isAdmin && <PaymentDetailsPreview paymentDetails={values.paymentDetails} />}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="row g-3 justify-content-end">
                                                            <div className="col-sm-6">
                                                                <GrandTotal amount={calculateGrandTotal(values)} />
                                                            </div>
                                                            {isAdmin && (
                                                                <div className="col-sm-6">
                                                                    <PaymentTypeSelect
                                                                        onChange={(e) => handlePaymentTypeChange(e, setFieldValue)}
                                                                        onEditDetails={() => setShowModal(true)}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <SubmitBar
                                                paymentType={values.paymentType}
                                                loading={loading}
                                                showCancel={!isUser}
                                                onCancel={() => handleCancel(resetForm)}
                                            />
                                        </div>
                                    </Form>

                                    {showModal && (
                                        <PaymentModalComponent
                                            selectedPaymentType={selectedPaymentType}
                                            onClose={() => setShowModal(false)}
                                            setPaymentDetails={(data) => {
                                                setFieldValue("paymentDetails", data);
                                                setShowModal(false);
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </Formik>
                    </>
                )}
            </div>
        </div>
    );
};

export default GenerateReceiptComponent;
